import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/react';
import { gsap, ScrollTrigger } from 'gsap/all';
import { fullHeight, useHsHeader } from '../../util/hsHeader';
import AudioPlayer, { AudioPlayerMobile } from '../AudioPlayer';
import { useDeviceSize } from '../../util/useDeviceSize';
import { centered, AnimatedTextWrapper } from '../../styles/helpers';
import { P } from '../../styles/typography';
import { FadeInAnimation } from '../../effects/withAnimation';

gsap.registerPlugin(ScrollTrigger);

export default function AudioSlide3({ audio, copy }) {
  const [{ headerHeight }] = useHsHeader();
  const { isWideScreen } = useDeviceSize();
  const [isAudioFocused, setIsAudioFocused] = useState(false);

  const slideTriggerRef = useRef();
  const copyRef = useRef();
  const audioRef = useRef();

  useEffect(() => {
    if (!isWideScreen) {
      return undefined;
    }

    const tl = gsap
      .timeline()
      .to(slideTriggerRef.current, {
        x: 0,
        duration: 0.2,
        ease: 'ease-in',
      })
      .to(audioRef.current, {
        css: {
          pointerEvents: 'auto',
          opacity: 1,
        },
        duration: 0,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(copyRef.current, {
        opacity: 1,
        duration: 0.2,
        delay: 0.2,
      })
      .to(slideTriggerRef.current, {
        duration: 0.2,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(slideTriggerRef.current, {
        duration: 0.01,
      });

    const st = ScrollTrigger.create({
      animation: tl,
      trigger: slideTriggerRef.current,
      pin: true,
      pinSpacing: false,
      scrub: true,
      start: 'top top',
      end: `bottom bottom`,
      anticipatePin: 1,
    });

    return () => {
      tl.kill();
      st.kill();
    };
  }, [headerHeight, isWideScreen]);

  if (isWideScreen) {
    return (
      <div
        css={css`
          ${fullHeight(2)};
          position: relative;
          z-index: 2;
          transform: translateY(-30vh) translateX(100%);
          display: flex;
        `}
        ref={slideTriggerRef}
      >
        <div
          css={css`
            ${fullHeight()};
            width: 70%;
            pointer-events: none;
            display: flex;
            flex-direction: column;
            justify-content: space-around;
          `}
        >
          <P
            css={css`
              max-width: 830px;
              transform: translateX(10%); // translateY(-50%);
              /* margin-top: 30vh; */
            `}
          >
            {copy[0].text}
          </P>
          <P
            ref={copyRef}
            css={css`
              max-width: 600px;
              transform: translateX(10%);
              align-self: center;
              opacity: 0;
            `}
          >
            {copy[1].text}
          </P>
        </div>

        <div
          css={css`
            width: 30%;
            ${fullHeight()};
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;
          `}
        >
          <div
            css={css`
              ${centered};
              width: 100%;
              height: 50%;
              opacity: 0;
              transition: opacity 0.2s ease-in;
              pointer-events: none;
            `}
            ref={audioRef}
          >
            <AudioPlayer url={audio.url} isFocused={isAudioFocused} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AnimatedTextWrapper>
        <P mobile>{copy[0].text}</P>
      </AnimatedTextWrapper>

      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <FadeInAnimation>
          <AudioPlayerMobile url={audio.url} />
        </FadeInAnimation>
      </div>

      <AnimatedTextWrapper>
        <P mobile>{copy[1].text}</P>
      </AnimatedTextWrapper>
    </div>
  );
}

AudioSlide3.propTypes = {
  audio: PropTypes.shape({ url: PropTypes.string }),
  copy: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.node })),
};

AudioSlide3.defaultProps = {
  audio: null,
  copy: null,
};
